import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { packageFeedsApi } from '@/api/packageFeeds'
import { useTenantKey } from './useTenantKey'
import type { SaveFeedRequest } from '@/types'

export function usePackageFeeds(projectId: string | undefined, enabled = true) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId ?? '', 'feeds'),
    queryFn: () => packageFeedsApi.getAll(projectId!),
    enabled: enabled && !!projectId,
  })
}

export function useCreatePackageFeed(projectId: string | undefined) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (feed: SaveFeedRequest) => packageFeedsApi.create(projectId!, feed),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId ?? '', 'feeds') }),
  })
}

export function useUpdatePackageFeed(projectId: string | undefined) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: ({ feedId, feed }: { feedId: string; feed: SaveFeedRequest }) =>
      packageFeedsApi.update(projectId!, feedId, feed),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId ?? '', 'feeds') }),
  })
}

export function useDeletePackageFeed(projectId: string | undefined) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (feedId: string) => packageFeedsApi.delete(projectId!, feedId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId ?? '', 'feeds') }),
  })
}
